import { useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { AnimatePresence, motion } from 'framer-motion';

// Components
import { Button, Container, MaterialLoader, QueryPagination, SectionHeader } from '@components';
import ListCoverStoriesItem from './ListCoverStoriesItem';

// GraphQL
import { GetCoverStoriesQuery } from '@graphqlTypes';
import { CoverStoriesQuery } from '@/queries/CoverStory';

// Utils
import { useClientMount } from '@/providers/ClientMountProvider';
import { useViewSize } from '@/providers/ViewSizeContext';
import { useDebug } from '@/utils/useDebugLog';
import { easeOutQuad } from '@/core/easing';

// Styles
import styles from './ListCoverStories.module.scss';
import className from 'classnames/bind';
const cx = className.bind(styles);

// --------------------

const DEBUG_MANY_GAMES = false; // Use to simulate many items for pagination testing

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.085,
      delayChildren: 0,
    }
  },
  exit: {
    opacity: 0,
    transition: { duration: 0.2, ease: easeOutQuad }
  }
};

// Item animation variants
const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3, ease: easeOutQuad }
  },
  exit: { opacity: 0 }
};

interface ListCoverStoriesProps {
  coverStories: GetCoverStoriesQuery['coverStories'];
  notIn?: number[];
  headline: string;
  subhead?: string;
  labels?: CoverStoryOptions['coverStoryListLabels'];
  pageUri?: string;
}

export type ListCoverStory = GetCoverStoriesQuery['coverStories']['nodes'][number];

// -------------------- Component

export default function ListCoverStories({ coverStories: initialCoverStories, headline, subhead, labels, pageUri, notIn = [] }: ListCoverStoriesProps) {
  const { log } = useDebug({ name: 'ListCoverStories', bgColor: '#F2CB05', enabled: false });

  // -------------------- CMS data setup

  const Copy_ListLabels = {
    solvedSingular: labels?.solvedSingular || 'Missing Solved in 1 Tile',
    solvedPlural: labels?.solvedPlural || 'Missing Solved in {{COUNT}} Tiles',
    resume: labels?.resume || 'Missing Resume',
    lost: labels?.lost || 'Missing you lost',
    ctaPlayMore: labels?.playMore || 'Missing play more',
  };

  // State
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);

  // SSR guards
  const { isReady: isViewSizeReady, isDesktop } = useViewSize();
  const { mounted } = useClientMount();

  const [getCoverStories] = useLazyQuery<GetCoverStoriesQuery>(CoverStoriesQuery);

  // --------------------- Pagination

  // Debug: Duplicate game data for pagination testing
  const debugCoverStories = useMemo(() => {
    if (!DEBUG_MANY_GAMES || !initialCoverStories?.nodes) return initialCoverStories;

    const originalNodes = initialCoverStories.nodes;
    const duplicatedNodes = [];

    // Duplicate 10 times to create ~150 items for testing
    for (let i = 0; i < 10; i++) {
      duplicatedNodes.push(
        ...originalNodes.map((node, index) => ({
          ...node,
          uri: `${node.uri}-duplicate-${i}-${index}`,
          title: `${node.title} (Copy ${i + 1})`,
        }))
      );
    }

    return {
      ...initialCoverStories,
      nodes: duplicatedNodes,
      pageInfo: {
        offsetPagination: {
          hasMore: true,
          total: duplicatedNodes.length,
        }
      }
    };
  }, [initialCoverStories]);

  // Debug: Decide which cover stories data to use
  const processedCoverStories = DEBUG_MANY_GAMES ? debugCoverStories : initialCoverStories;

  // ----------

  const PER_PAGE = 15; // Pagination: items per page

  // Initialize with only the first page of items
  const initialPageItems = useMemo(() => {
    return processedCoverStories?.nodes?.slice(0, PER_PAGE) || [];
  }, [processedCoverStories]);

  // Current page items
  const [coverStories, setCoverStories] = useState(initialPageItems);

  // Extract pagination info
  const { pageInfo: { offsetPagination: { hasMore, total }} } = processedCoverStories || { pageInfo: { offsetPagination: { hasMore: false, total: 0 }}};

  // Determine if pagination controls should be shown
  const showPaging = useMemo(
    () => processedCoverStories?.pageInfo.offsetPagination.hasMore,
    [processedCoverStories]
  );

  // Total count for pagination
  const totalCount = useMemo(
    () => processedCoverStories?.pageInfo.offsetPagination.total || 0,
    [processedCoverStories]
  );

  // Fetch cover stories for a specific page
  const fetchCoverStories = async (page: number) => {
    setLoading(true);
    setCurrentPage(page);

    // Debug: Simulate pagination with duplicated data
    if (DEBUG_MANY_GAMES && processedCoverStories?.nodes) {
      const allNodes = processedCoverStories.nodes;
      const startIndex = (page - 1) * PER_PAGE;
      const endIndex = startIndex + PER_PAGE;
      const pageNodes = allNodes.slice(startIndex, endIndex);

      setTimeout(() => {
        setCoverStories(pageNodes);
        setLoading(false);
      }, 300); // Simulate network delay
      return;
    }

    const { data } = await getCoverStories({
      variables: {
        offset: (page - 1) * PER_PAGE,
        notIn,
      },
    });
    if (data?.coverStories.nodes) setCoverStories(data.coverStories.nodes);
    else setCoverStories([]);
    setLoading(false);
  };

  // -------------------- Render

  // Prevent SSR/hydration mismatches
  const isClientReady = mounted && isViewSizeReady;

  log('Render', { headline, subhead, total, hasMore, coverStoriesCount: coverStories.length, isClientReady });

  return (
    <section className={cx(["list-cover-stories"])}>
      <Container>
        <SectionHeader headline={headline} subhead={subhead} />
        <div className={cx(["list"])}>
          {loading && <MaterialLoader />}
          {!loading && isClientReady && (
            <AnimatePresence mode="wait">
              <motion.div
                key={currentPage}
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className={cx("list-cover-stories__items-wrapper")}
              >
                {coverStories.map((coverStory, index) => (
                  <ListCoverStoriesItem
                    key={coverStory.uri}
                    {...coverStory}
                    variants={itemVariants}
                    solvedLabelSingular={Copy_ListLabels.solvedSingular}
                    solvedLabelPlural={Copy_ListLabels.solvedPlural}
                    lostLabel={Copy_ListLabels.lost}
                  />
                ))}
              </motion.div>
            </AnimatePresence>
          )}
        </div>
        {isClientReady && pageUri && (
          <div className={cx("list-cover-stories__cta")}>
            <Button label={Copy_ListLabels.ctaPlayMore} url={pageUri} />
          </div>
        )}
        {isClientReady && !pageUri && showPaging && (
          <QueryPagination
            className={cx("list-cover-stories__pagination")}
            totalCount={totalCount}
            perPage={PER_PAGE}
            size="small"
            currentPage={currentPage}
            onPageChange={fetchCoverStories}
          />
        )}
      </Container>
    </section>
  );
}
